import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Filter, PlusCircle, Search, X } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { Badge } from '@/components/ui-kit/badge';
import { Separator } from '@/components/ui-kit/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import { Label } from '@/components/ui-kit/label';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui-kit/sheet';
import { Calendar } from '@/components/ui-kit/calendar';
import { cn, deepEqual, debounce, formatDate } from '@/lib/utils';

type Option = { label: ReactNode; value: string };

type FilterType = 'SearchInput' | 'MultiSelect' | 'Radio' | 'DateRange' | 'DropdownSearchInput';

type FilterItem<T extends Record<string, unknown>> = {
  key: keyof T;
  type: FilterType;
  label: string;
  props?: {
    options?: Option[];
    placeholder?: string;
    className?: string | Record<string, string>;
  };
};

export type FilterChangeHandler<T extends Record<string, unknown>> = <K extends keyof T>(
  key: K,
  value: T[K],
  values: T
) => void;

type FilterToolbarProps<T extends Record<string, unknown>> = {
  filters: FilterItem<T>[];
  values: T;
  defaultValues: T;
  onChange: FilterChangeHandler<T>;
  onReset?: (values?: T) => void;
  hideGlobalResetButton?: boolean;
};

const ResetButton = ({ onClick }: { onClick: () => void }) => (
  <Button type="button" variant="outline" size="sm" onClick={onClick}>
    Reset
    <X className="h-4 w-4" />
  </Button>
);

const SearchInput = ({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) => {
  const [state, setState] = useState(value);
  const debounced = useRef(debounce(onChange, 300)).current;

  useEffect(() => {
    setState(value);
  }, [value]);

  useEffect(() => () => debounced.cancel(), [debounced]);

  return (
    <div className="flex items-center rounded-sm border px-2">
      <Search className="mr-2 h-4 w-4 text-muted-foreground" />
      <Input
        value={state}
        placeholder={placeholder || 'Search...'}
        onChange={(event) => {
          setState(event.target.value);
          debounced(event.target.value);
        }}
        className={cn(
          'h-8 w-52 border-none p-0 focus-visible:ring-0 focus-visible:ring-offset-0',
          className
        )}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={cn('h-full p-1 pr-0 hover:bg-transparent', !state && 'invisible')}
        onClick={() => {
          setState('');
          onChange('');
        }}
      >
        <X className="h-4 w-4 text-muted-foreground" />
      </Button>
    </div>
  );
};

const MultiSelect = ({
  label,
  options = [],
  value = [],
  onChange,
}: {
  label: string;
  options?: Option[];
  value?: string[];
  onChange: (value: string[]) => void;
}) => {
  const selectedValues = Array.isArray(value) ? value : [];

  const toggleValue = (optionValue: string) => {
    onChange(
      selectedValues.includes(optionValue)
        ? selectedValues.filter((item) => item !== optionValue)
        : [...selectedValues, optionValue]
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8 border-dashed">
          <PlusCircle className="h-4 w-4" />
          {label}
          {selectedValues.length > 0 && (
            <>
              <Separator orientation="vertical" className="mx-1 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                {selectedValues.length} selected
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        <div className="max-h-64 overflow-auto">
          {options.map((option) => {
            const selected = selectedValues.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent"
                onClick={() => toggleValue(option.value)}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                    selected ? 'bg-primary text-primary-foreground' : 'opacity-50'
                  )}
                >
                  {selected && <Check className="h-3 w-3" />}
                </span>
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
        {selectedValues.length > 0 && (
          <>
            <Separator />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-1 w-full"
              onClick={() => onChange([])}
            >
              Clear
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

const RadioFilter = ({
  label,
  options = [],
  value,
  onChange,
}: {
  label: string;
  options?: Option[];
  value?: string;
  onChange: (value: string | null) => void;
}) => {
  const selected = options.find((option) => option.value === value);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8 border-dashed">
          <PlusCircle className="h-4 w-4" />
          {label}
          {selected && (
            <>
              <Separator orientation="vertical" className="mx-1 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                {selected.label}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        <RadioGroup value={value || ''} onValueChange={(nextValue) => onChange(nextValue)}>
          {options.map((option) => (
            <Label
              key={option.value}
              htmlFor={option.value}
              className="flex items-center gap-2 rounded-sm px-2 py-2 hover:bg-accent"
            >
              <RadioGroupItem id={option.value} value={option.value} />
              <span>{option.label}</span>
            </Label>
          ))}
        </RadioGroup>
        {value && (
          <>
            <Separator />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-1 w-full"
              onClick={() => onChange(null)}
            >
              Clear
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

const DateRangeFilter = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: { from?: Date; to?: Date } | null;
  onChange: (value: { from?: Date; to?: Date } | null) => void;
}) => {
  const [date, setDate] = useState<{ from?: Date; to?: Date } | null>(value ?? null);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8 border-dashed">
          {label}
          {value?.from && (
            <>
              <Separator orientation="vertical" className="mx-1 h-4" />
              <span className="text-xs">
                {formatDate(value.from, true)}
                {value.to ? ` - ${formatDate(value.to, true)}` : ''}
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          initialFocus
          mode="range"
          selected={date?.from ? { from: date.from, to: date.to } : undefined}
          onSelect={(nextDate) => setDate(nextDate ?? null)}
          numberOfMonths={1}
        />
        <div className="flex gap-2 p-3 pt-0">
          <Button type="button" variant="outline" className="w-full" onClick={() => setDate(null)}>
            Reset
          </Button>
          <Button type="button" className="w-full" onClick={() => onChange(date)}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const FilterToolbar = <T extends Record<string, unknown>>({
  filters,
  values,
  defaultValues,
  onChange,
  onReset,
  hideGlobalResetButton = false,
}: FilterToolbarProps<T>) => {
  const initialValuesRef = useRef(defaultValues);

  const changeHandler = <K extends keyof T>(key: K, value: T[K]) => {
    onChange(key, value, { ...values, [key]: value });
  };

  const controllers = useMemo(
    () =>
      filters.map((filter) => {
        const key = filter.key as keyof T;
        const value = values[key];
        const options = filter.props?.options;

        if (filter.type === 'SearchInput' || filter.type === 'DropdownSearchInput') {
          return (
            <SearchInput
              key={String(key)}
              value={(value as string) ?? ''}
              placeholder={filter.props?.placeholder}
              className={filter.props?.className as string}
              onChange={(nextValue) => changeHandler(key, nextValue as T[keyof T])}
            />
          );
        }

        if (filter.type === 'MultiSelect') {
          return (
            <MultiSelect
              key={String(key)}
              label={filter.label}
              options={options}
              value={value as string[]}
              onChange={(nextValue) => changeHandler(key, nextValue as T[keyof T])}
            />
          );
        }

        if (filter.type === 'Radio') {
          return (
            <RadioFilter
              key={String(key)}
              label={filter.label}
              options={options}
              value={value as string}
              onChange={(nextValue) => changeHandler(key, nextValue as T[keyof T])}
            />
          );
        }

        return (
          <DateRangeFilter
            key={String(key)}
            label={filter.label}
            value={value as { from?: Date; to?: Date } | null}
            onChange={(nextValue) => changeHandler(key, nextValue as T[keyof T])}
          />
        );
      }),
    [filters, values, changeHandler]
  );

  const showReset = !hideGlobalResetButton && !deepEqual(initialValuesRef.current, values);
  const reset = () => onReset?.(initialValuesRef.current);

  const content = (items: ReactNode[]) => (
    <>
      {items}
      {showReset && <ResetButton onClick={reset} />}
    </>
  );

  return (
    <>
      <div className="hidden flex-wrap items-center gap-4 md:flex">{content(controllers)}</div>
      <div className="flex items-center justify-between gap-2 md:hidden">
        <div className="min-w-0 max-w-72 flex-1">{controllers[0]}</div>
        {controllers.length > 1 && (
          <Sheet>
            <SheetTrigger asChild>
              <Button type="button" variant="outline" size="sm" className="h-8 w-8 p-0">
                <Filter className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full">
              <SheetTitle className="mb-4">Filter</SheetTitle>
              <SheetDescription />
              <div className="flex flex-col space-y-4">
                {content(controllers.slice(1))}
                <SheetClose asChild>
                  <Button type="button" size="sm">
                    Show Results
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>
    </>
  );
};
